import mParticle from '@mparticle/web-sdk';
import BrazeKit from '@mparticle/web-braze-kit';
import BingKit from '@mparticle/web-bing-ads-kit';
import TwitterKit from '@mparticle/web-twitter-kit';
import FacebookKit from '@mparticle/web-facebook-kit';
import OneTrustKit from '@mparticle/web-onetrust-kit';
import GoogleAdsKit from '@mparticle/web-adwords-kit';
import GoogleTagManagerKit from '@mparticle/web-google-tag-manager-kit';
import GoogleAnalytics4Kit from '@mparticle/web-google-analytics-4-client-kit/dist/GoogleAnalytics4EventForwarderClientSide-Kit.common';
import { validateAttributeAndRename } from './validator';
import { generateCookie, updateUserAttributes } from './cookies/generateCookie';

export interface IEnabledPlugins {
  googleTagManager: boolean;
  braze: boolean;
  googleAnalytics4: boolean;
  bing: boolean;
  twitter: boolean;
}

const PAGE_VIEW_EVENT = 'Page View Web';
const WEB_PLATFORM_PAGE_VIEW_EVENT = 'Web Platform Page View';
const FALCON_PAGE_VIEW_EVENT = 'Web Platform Falcon Page View';
const PLATFORM_PAGE_VIEW_EVENTS: Record<string, string> = {
  '/client/homepage.aspx': 'Homepage',
  '/clients/homepage.aspx': 'Homepage',
  '/trade/fullticket': 'Order Ticket',
  '/manage/profitloss': 'Profit & Loss',
  '/manage/transferfunds': 'Transfer Funds',
  '/trade/quickticket': 'Quick Order Ticket',
  '/trade/depositinstructions.aspx': 'Deposit Funds'
};

const FALCON_PAGE_VIEW_EVENTS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/trading/Manage/ProfitLoss': 'Profit & Loss',
  '/trading/Manage/TradingAccountsInfo': 'Account',
  '/trading/stockmanager/Wizard.aspx': 'Watchlist',
  '/trading/Manage/Holding': 'Holdings',
  '/trading/manage/products': 'Instruments',
  '/trading/Trade/FullTicket': 'Ticket',
  '/trading/Manage/OrderTicket': 'Ticket',
  '/trading/clients/homepage.aspx': 'Markets',
  '/trading/usersettings/clientdetails.aspx': 'Personal Details'
};

/**
 * Logout user associating a new anonymous identifier
 * https://docs.mparticle.com/developers/sdk/web/idsync/#login-and-logout
 */
const reset = (args: any) => {
  const { config } = args;
  mParticle.Identity.logout(
    {
      userIdentities: {}
    },
    config.mParticle.identityCallback
  );
};

const filterAttributesList = (attributes: mParticle.SDKEventAttrs) => {
  const newAttributes: any = {};
  for (const [key, value] of Object.entries(attributes)) {
    const keyword = validateAttributeAndRename(key);
    if (keyword) {
      newAttributes[keyword] = value; // replace with standardised name
    }
  }
  return newAttributes;
};

const page = (args: any) => {
  const { payload } = args;
  //If login is required and user hasn't been identified yet, don't send event, will be sent in callback for identify instead.
  if (payload.options?.requireLoggedIn && !mParticle.Identity.getCurrentUser()?.isLoggedIn()) {
    return;
  }
  const searchParams = new URLSearchParams(payload.properties.search);
  let attributes: mParticle.SDKEventAttrs = {};
  for (const [key, value] of searchParams.entries()) {
    attributes[key] = value;
  }
  attributes = { ...attributes, ...payload.properties };

  // filter out attributes in blacklist and standardise names
  const filteredAttributes = filterAttributesList(attributes);
  mParticle.logPageView(PAGE_VIEW_EVENT, filteredAttributes, {
    'Google.Page': payload.properties.url,
    'Google.Title': payload.properties.title
  });

  const userAttributes = mParticle.Identity.getCurrentUser()?.getAllUserAttributes();

  if (userAttributes?.Falcon === true) {
    for (const prefix in FALCON_PAGE_VIEW_EVENTS) {
      if (payload.properties.path.toLowerCase().startsWith(prefix.toLowerCase())) {
        let tradeType = undefined;
        if (payload.properties.search?.toLowerCase().includes('buy')) {
          tradeType = 'Buy';
        } else if (payload.properties.search?.toLowerCase().includes('sell')) {
          tradeType = 'Sell';
        }
        mParticle.logEvent(
          `${FALCON_PAGE_VIEW_EVENT} - ${tradeType === undefined ? '' : tradeType + ' '}${FALCON_PAGE_VIEW_EVENTS[prefix]}`,
          mParticle.EventType.Navigation,
          {},
          {}
        );
      }
    }
  } else {
    /** Send a special event for some pages to use in Braze */
    for (const prefix in PLATFORM_PAGE_VIEW_EVENTS) {
      if (payload.properties.path.toLowerCase().startsWith(prefix.toLowerCase())) {
        mParticle.logEvent(`${WEB_PLATFORM_PAGE_VIEW_EVENT} - ${PLATFORM_PAGE_VIEW_EVENTS[prefix]}`, mParticle.EventType.Navigation, {}, {});
      }
    }
  }
};

const track = (args: any) => {
  const { payload } = args;
  mParticle.logEvent(payload.event, payload.options.type, payload.properties);
};

export const identityCallbackFn = (config: any, payload: any): mParticle.IdentityCallback => {
  return (result: mParticle.IdentityResult) => {
    if (result.httpCode < 0) {
      console.error(`mParticle: ${result.body}`);
      return;
    }
    const user = result.getUser();
    if (user) {
      user.setUserAttributes(payload.traits);
      page({
        payload: {
          properties: {
            url: globalThis.location.toString(),
            path: location.pathname,
            host: globalThis.location.hostname,
            title: document.title
          },
          options: {}
        }
      });
    } else {
      console.error(`mParticle: user not identified`);
    }
    if (config.mParticle.identityCallback) {
      config.mParticle.identityCallback(result);
    }
  };
};

export const setUTMParams = (params: { utm_campaign?: string; utm_medium?: string; utm_source?: string }) => {
  if (!(params.utm_campaign || params.utm_medium || params.utm_source)) {
    // no utm attribute
    return;
  }
  const utmValues = `${params.utm_campaign ?? ''}_${params.utm_medium ?? ''}_${params.utm_source ?? ''}`;
  let currentUser = mParticle.Identity.getCurrentUser();

  if (currentUser === undefined) {
    throw new Error('mParticle - User is not identified when setting UTM params');
  }

  let userAttributesLists = currentUser?.getUserAttributesLists();
  //append to existing list
  let utmList: mParticle.UserAttributesValue[] = userAttributesLists?.utm ?? [];
  utmList.unshift(utmValues);
  currentUser.setUserAttributeList('utm', utmList);

  // Append campaigns to user attributes
  let utmCampaignAll: mParticle.UserAttributesValue[] = userAttributesLists?.utm_campaign_all ?? [];
  if (params.utm_campaign) utmCampaignAll.unshift(params.utm_campaign);
  currentUser.setUserAttributeList('utm_campaign_all', utmCampaignAll);

  let utmMediumAll: mParticle.UserAttributesValue[] = userAttributesLists?.utm_medium_all ?? [];
  if (params.utm_medium) utmMediumAll.unshift(params.utm_medium);
  currentUser.setUserAttributeList('utm_medium_all', utmMediumAll);

  let utmSourceAll: mParticle.UserAttributesValue[] = userAttributesLists?.utm_source_all ?? [];
  if (params.utm_source) utmSourceAll.unshift(params.utm_source);
  currentUser.setUserAttributeList('utm_source_all', utmSourceAll);

  // Store latest utm
  currentUser.setUserAttributes({
    utm_campaign: params.utm_campaign,
    utm_medium: params.utm_medium,
    utm_source: params.utm_source
  });
};

const identify = (args: any) => {
  const { payload, config } = args;
  const currentUser = mParticle.Identity.getCurrentUser();
  // if same user is already logged in
  if (currentUser?.isLoggedIn() && currentUser?.getUserIdentities()?.userIdentities?.customerid === payload.userId) {
    return;
  }

  // if user is logged in but the customerId is different. End old session first before login
  if (currentUser?.isLoggedIn()) {
    mParticle.endSession();
  }

  const identityCallback = identityCallbackFn(config, payload);
  mParticle.Identity.login(
    {
      userIdentities: {
        customerid: payload.userId
      }
    },
    identityCallback
  );
};

export const mParticlePlugin = (apiKey: string, mParticleConfig: mParticle.MPConfiguration, enabledPlugins: IEnabledPlugins) => {
  let mParticleIsReady = false;

  // read mdevid from the search path for cross-domain sessions
  const urlSearchParams = new URLSearchParams(globalThis.location.search);
  const params = Object.fromEntries(Object.entries(Object.fromEntries(urlSearchParams.entries())).map(([k, v]) => [k.toLowerCase(), v]));
  mParticleConfig.deviceId = params.mdevid;

  try {
    generateCookie();
  } catch {
    console.error('UTM Cookie Error');
  }

  // return object for analytics to use
  return {
    name: `mParticle`,
    config: {
      apiKey,
      mParticle: mParticleConfig,
      enabledPlugins
    },
    methods: {
      setUserAttribute(attribute: string, value: string) {
        mParticle.Identity.getCurrentUser().setUserAttribute(attribute, value);
      }
    },
    initialize: (args: any) => {
      const { config } = args;
      if (config.enabledPlugins.braze) {
        BrazeKit.register(config.mParticle);
      }
      if (config.enabledPlugins.bing) {
        BingKit.register(config.mParticle);
      }
      if (config.enabledPlugins.googleTagManager) {
        GoogleTagManagerKit.register(config.mParticle);
      }
      if (config.enabledPlugins.googleAnalytics4) {
        GoogleAnalytics4Kit.register(config.mParticle);
      }
      if (config.enabledPlugins.twitter) {
        TwitterKit.register(config.mParticle);
      }
      if (config.enabledPlugins.facebook) {
        FacebookKit.register(config.mParticle);
      }
      if (config.enabledPlugins.googleAds) {
        GoogleAdsKit.register(config.mParticle);
      }
      if (config.enabledPlugins.oneTrust) {
        OneTrustKit.register(config.mParticle);
      }

      if (config.mParticle.identityCallback === undefined) {
        config.mParticle.identityCallback = () => {
          try {
            mParticleIsReady = true;
            setUTMParams(params);
          } catch (error) {
            console.error(error);
          }
        };
      }

      config.mParticle.onCreateBatch = (batch: any) => {
        if (batch.events.length === 0) {
          return undefined;
        }

        batch.events = batch.events.filter((event: any) => {
          return event.event_type !== 'application_state_transition' || event.data.is_first_run;
        });
        return batch;
      };

      mParticle.init(config.apiKey, config.mParticle);
      // mParticle.setLogLevel(`verbose`);
      mParticle.ready(() => {
        try {
          mParticleIsReady = true;
          updateUserAttributes();
          setUTMParams(params);
        } catch (error) {
          console.error(error);
        }
      });
    },
    page,
    track,
    identify,
    reset,
    loaded: () => {
      return mParticleIsReady;
    }
  };
};
// eslint-disable-next-line import/no-default-export
export default mParticlePlugin;
